import { useEffect, useState } from 'react';

/**
 * B309 fix round 2 — is the week the page is holding a DIFFERENT week from the
 * one it is now about?
 *
 * Today keeps the last week on screen while a refetch of the SAME week runs
 * (so an open day panel survives a write). At a week rollover that rule would
 * be wrong: `useWeeklyPlan` keeps the previous week's rows until the new
 * week's fetch lands and re-dates them with the new week's dates, so the page
 * would show last week's sessions under this week's days. This says so, and
 * the caller treats the held data as not there (the skeleton shows) until the
 * new week has actually loaded.
 *
 * `weekKey` identifies the week being asked for (season and week number).
 * Data present when the hook mounts is taken to belong to that first key. A
 * later change of key makes the held data stale at once, in the same render
 * (no frame of old rows under new dates), and it stays stale until a load
 * under the new key has been seen to finish (`loading` true, then false), or
 * a different plan row has arrived (`dataId`, which unlike the day rows is not
 * re-derived from the new dates). No held data means nothing is stale, so a
 * rollover into a week with no plan cannot get stuck.
 */
export function useStaleWeek(
  weekKey: string,
  loading: boolean,
  dataId: string | null | undefined,
  hasData: boolean,
): boolean {
  const id = dataId ?? null;
  const [held, setHeld] = useState({ key: weekKey, id });
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const mismatch = held.key !== weekKey;

  useEffect(() => {
    if (loading) setLoadingKey(weekKey);
  }, [loading, weekKey]);

  useEffect(() => {
    if (!mismatch || loading) return;
    if (loadingKey === weekKey || id !== held.id) setHeld({ key: weekKey, id });
  }, [mismatch, loading, loadingKey, weekKey, id, held.id]);

  return hasData && mismatch;
}
